import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, FalhaDaApi } from '../../api/cliente';
import { Alerta, Botao } from '../../componentes/base';
import { AreaDeTexto, Campo, Seletor } from '../../componentes/campos';
import { Modal } from '../../componentes/Modal';
import { useSessaoPortal } from '../SessaoPortal';

/** Tudo texto: o formulário guarda o que foi digitado, a API é quem interpreta. */
const EM_BRANCO = {
  clinicaId: '',
  nome: '',
  documento: '',
  telefone: '',
  telefoneExtra: '',
  email: '',
  valorOriginal: '',
  vencimentoOriginal: '',
  descricaoDivida: '',
  pastaDocumentos: '',
  logradouro: '',
  cidade: '',
  uf: '',
  cep: '',
  observacoes: '',
};

type Formulario = typeof EM_BRANCO;

/**
 * A clínica cadastra um cliente novo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  O QUE ESTE FORMULÁRIO NÃO TEM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Não há campo de fase: o caso nasce em "aguardando contato", porque cadastrar aqui
 * é ENTREGAR o caso ao escritório, não movê-lo pelo funil. Não há responsável: quem
 * designa a colaboradora é o escritório.
 *
 * As duas ausências são a regra de segurança, e ela mora no servidor — esta tela
 * apenas não oferece o que a API não aceitaria.
 *
 * A CLÍNICA só aparece quando o acesso cobre mais de uma — o dentista com duas
 * razões sociais precisa dizer em qual carteira o caso entra, e errar isso é mandar
 * o caso ao judicial pelo CNPJ errado. Com uma carteira só, o campo não existe: a
 * recepção de quem tem uma clínica não deve escolher nada. O servidor confere a
 * escolha contra a sessão de qualquer forma.
 */
export function ModalNovoCliente({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const { token = '' } = useParams();
  const { clinicas } = useSessaoPortal();
  const precisaEscolherCarteira = clinicas.length > 1;
  const navegar = useNavigate();
  const clienteConsulta = useQueryClient();
  const [dados, setDados] = useState<Formulario>(EM_BRANCO);
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});

  function definir(campo: keyof Formulario, valor: string) {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    // O erro sai assim que a pessoa mexe no campo: mantê-lo até o próximo envio
    // deixa "Informe o nome" embaixo de um nome já digitado.
    setInvalidos((atual) => {
      if (!atual[campo]) return atual;
      const { [campo]: _corrigido, ...resto } = atual;
      return resto;
    });
  }

  function fechar() {
    setDados(EM_BRANCO);
    setErro(null);
    setInvalidos({});
    aoFechar();
  }

  const cadastrar = useMutation({
    mutationFn: () => {
      // Campo vazio é campo NÃO informado. Mandar `""` faria a API tentar
      // interpretar o vazio como valor e recusar o cadastro por isso.
      const corpo = Object.fromEntries(
        Object.entries(dados).filter(([, valor]) => valor.trim() !== ''),
      );
      return api.criar<{ id: string; nome: string }>('/portal/devedores', corpo);
    },
    onSuccess: (cliente) => {
      void clienteConsulta.invalidateQueries({ queryKey: ['portal'] });
      fechar();
      // Cadastrar é só metade do trabalho: o passo seguinte é sempre anexar o
      // contrato e a ficha. Levar para a ficha evita procurar o nome logo depois.
      void navegar(`/portal/${token}/clientes/${cliente.id}`);
    },
    onError: (e) => {
      if (e instanceof FalhaDaApi) {
        setErro(e.message);
        setInvalidos(e.camposInvalidos ?? {});
        return;
      }
      setErro('Não foi possível cadastrar o cliente.');
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string[]> = {};
    if (dados.nome.trim().length < 2) faltando['nome'] = ['Informe o nome do cliente.'];
    if (precisaEscolherCarteira && !dados.clinicaId) {
      faltando['clinicaId'] = ['Escolha em qual clínica este cliente entra.'];
    }
    if (Object.keys(faltando).length > 0) {
      setErro(null);
      setInvalidos(faltando);
      return;
    }
    cadastrar.mutate();
  }

  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo="Cadastrar cliente"
      descricao="O caso entra na fila do escritório assim que você salvar."
      largura="44rem"
      rodape={
        <>
          <Botao onClick={fechar} desabilitado={cadastrar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={cadastrar.isPending}
          >
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar e anexar documentos'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-5">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        {precisaEscolherCarteira && (
          <Seletor
            rotulo="Clínica"
            valor={dados.clinicaId}
            aoMudar={(v) => definir('clinicaId', v)}
            vazio="Selecione…"
            opcoes={clinicas.map((c) => ({ valor: c.id, rotulo: c.nome }))}
            {...erroDe('clinicaId')}
          />
        )}

        <Campo
          rotulo="Nome completo"
          valor={dados.nome}
          aoMudar={(v) => definir('nome', v)}
          placeholder="Maria Aparecida de Souza"
          {...erroDe('nome')}
        />

        <Campo
          rotulo="CPF"
          valor={dados.documento}
          aoMudar={(v) => definir('documento', v)}
          placeholder="000.000.000-00"
          auxilio="Dígito errado não impede o cadastro — o escritório confere depois."
          {...erroDe('documento')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Telefone"
            valor={dados.telefone}
            aoMudar={(v) => definir('telefone', v)}
            placeholder="(14) 98142-7809"
            auxilio="É por aqui que a cobrança fala com o cliente."
            {...erroDe('telefone')}
          />
          <Campo
            rotulo="Telefone alternativo"
            valor={dados.telefoneExtra}
            aoMudar={(v) => definir('telefoneExtra', v)}
            placeholder="opcional"
            {...erroDe('telefoneExtra')}
          />
        </div>

        <Campo
          rotulo="E-mail"
          tipo="email"
          valor={dados.email}
          aoMudar={(v) => definir('email', v)}
          placeholder="opcional"
          {...erroDe('email')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Valor devido"
            valor={dados.valorOriginal}
            aoMudar={(v) => definir('valorOriginal', v)}
            placeholder="1.850,00"
            auxilio="O valor na origem, não o saldo já negociado."
            {...erroDe('valorOriginal')}
          />
          <Campo
            rotulo="Vencimento"
            tipo="date"
            valor={dados.vencimentoOriginal}
            aoMudar={(v) => definir('vencimentoOriginal', v)}
            {...erroDe('vencimentoOriginal')}
          />
        </div>

        <Campo
          rotulo="O que ficou devendo"
          valor={dados.descricaoDivida}
          aoMudar={(v) => definir('descricaoDivida', v)}
          placeholder="Tratamento ortodôntico — 6 parcelas em aberto"
          {...erroDe('descricaoDivida')}
        />

        <fieldset className="space-y-4 border-t pt-5">
          <legend className="sr-only">Endereço</legend>
          <p className="text-xs text-[color:var(--texto-tenue)]">
            Endereço — dispensável agora, indispensável para citar o cliente se o caso
            precisar ir ao judicial.
          </p>

          <Campo
            rotulo="Logradouro"
            valor={dados.logradouro}
            aoMudar={(v) => definir('logradouro', v)}
            placeholder="Rua, número, complemento"
            {...erroDe('logradouro')}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_6rem_9rem]">
            <Campo
              rotulo="Cidade"
              valor={dados.cidade}
              aoMudar={(v) => definir('cidade', v)}
              {...erroDe('cidade')}
            />
            <Campo
              rotulo="UF"
              valor={dados.uf}
              aoMudar={(v) => definir('uf', v.toUpperCase())}
              placeholder="SP"
              {...erroDe('uf')}
            />
            <Campo
              rotulo="CEP"
              valor={dados.cep}
              aoMudar={(v) => definir('cep', v)}
              placeholder="14780-000"
              {...erroDe('cep')}
            />
          </div>
        </fieldset>

        <Campo
          rotulo="Pasta no Drive (opcional)"
          valor={dados.pastaDocumentos}
          aoMudar={(v) => definir('pastaDocumentos', v)}
          placeholder="https://drive.google.com/drive/folders/…"
          auxilio="Contrato e documentos ficam lá; aqui guardamos só o caminho."
          {...erroDe('pastaDocumentos')}
        />

        <AreaDeTexto
          rotulo="Observações para o escritório"
          valor={dados.observacoes}
          aoMudar={(v) => definir('observacoes', v)}
          linhas={3}
          placeholder="Qualquer contexto que ajude na cobrança."
        />
      </form>
    </Modal>
  );
}
